/**
 * ═══════════════════════════════════════════════════════════════
 * SAT Generator 3.0 — Native SVG Graph & Cartesian Coordinate Plane
 * PR-D4-020 / PR-D4-033: Standalone SVG renderer for SAT Math
 * coordinate planes, functions, geometry & equations.
 *
 * Architectural SSOT: docs/Global_Graphic_Standards.md (GGS v2.2.0 — single source of truth)
 * Agent gate:         docs/agent-configs/ggs-invariant.md
 *
 * GGS mapping (Option 1 — fixed canvas, internal clearance):
 *   - viewBox "0 0 500 500"           → GGS §3.1 / G24_FIXED_VIEWBOX
 *   - width="100%" + xMidYMid meet    → GGS §3.1.2
 *   - GRAPH_SAFETY_MARGIN (≥30px)     → internal inset; NOT viewBox expansion
 *   - CARTESIAN_PLOT_PADDING (≥30px)  → plot/label clearance inside canvas
 *   - stroke widths / point radii     → GGS §3.3.2 visual absolutes
 *   - text-anchor + dominant-baseline → GGS §4.2.2 label anchoring
 * ═══════════════════════════════════════════════════════════════
 */

import { useMemo } from 'react';
import type { GraphData, GraphViewport, GraphExpression, InteractivePoint } from '../lib/types';

interface NativeGraphRendererProps {
    graphData?: GraphData | null;
    svgFallback?: string | null;
    className?: string;
    height?: number | string;
    width?: number | string;
}

/** GGS §3.1 — standard internal canvas size. */
export const GGS_CANVAS_SIZE = 500;

/** GGS §3.1 / G24 — fixed viewBox (never expanded for padding). */
export const GGS_VIEWBOX = `0 0 ${GGS_CANVAS_SIZE} ${GGS_CANVAS_SIZE}`;

/** Explicit ink — never inherit `currentColor` (PR-D4-037). */
export const GGS_INK_HEX = '#0F172A';

/** Canvas plate fill — self-contained light surface (GGS §3.2 Layer 1). */
export const GGS_BG_HEX = '#FFFFFF';

/**
 * OS / theme isolation on the SVG root. `only light` is required so Chrome/Safari
 * auto-dark-mode cannot invert geometry (PR-D4-038).
 */
export const GGS_LIGHT_ISOLATION_STYLE =
    'background-color: #ffffff !important; color-scheme: only light !important; forced-color-adjust: none !important;';

/** Mandatory full-canvas background plate (GGS §3.2). */
export const GGS_BACKGROUND_PLATE = `<rect width="100%" height="100%" fill="${GGS_BG_HEX}"/>`;

/** Inline failsafe for the DOM light-barrier wrapper (PR-D4-038). */
export const GGS_WRAPPER_INLINE_STYLE = {
    backgroundColor: '#ffffff',
    colorScheme: 'only light',
    forcedColorAdjust: 'none',
} as const;

const GGS_DISPLAY_STYLE = `display:block;max-width:100%;height:auto;${GGS_LIGHT_ISOLATION_STYLE}`;

function hasGgsBackgroundPlate(svg: string): boolean {
    const headMatch = svg.match(/<svg\b[^>]*>([\s\S]{0,2500})/i);
    const head = headMatch?.[1] ?? '';
    const rects = head.match(/<rect\b[^>]*>/gi) ?? [];
    return rects.some((rect) => {
        const fill = /fill\s*=\s*["']#(?:ffffff|fff)["']/i.test(rect);
        const width = /width\s*=\s*["'](?:100%|500)["']/i.test(rect);
        const height = /height\s*=\s*["'](?:100%|500)["']/i.test(rect);
        return fill && width && height;
    });
}

/** Inject GGS Layer-1 white plate as the first child of `<svg>` if missing. */
export function ensureGgsBackgroundPlate(svg: string): string {
    if (hasGgsBackgroundPlate(svg)) return svg;
    return svg.replace(/<svg\b[^>]*>/i, (open) => `${open}\n${GGS_BACKGROUND_PLATE}`);
}

/** Replace CSS `currentColor` with deterministic GGS ink. */
export function stripCurrentColor(svg: string): string {
    return svg.replace(/currentColor/gi, GGS_INK_HEX);
}

/**
 * Removes presentation-only frame elements from server SVGs. Full-canvas strokes
 * and explicitly named brackets/frame decorations are chrome, not figure data.
 * Geometric corner markers (for example, right-angle symbols) are figure data
 * and therefore must survive this cleanup.
 */
export function stripDecorativeSvgFrame(svg: string): string {
    const withoutNamedDecorations = svg.replace(
        /<(?:path|line|polyline|polygon|g)\b(?=[^>]*\b(?:id|class)=["'][^"']*(?:bracket|frame|border)[^"']*["'])[^>]*>[\s\S]*?<\/(?:path|line|polyline|polygon|g)>|<(?:path|line|polyline|polygon)\b(?=[^>]*\b(?:id|class)=["'][^"']*(?:bracket|frame|border)[^"']*["'])[^>]*\/?>/gi,
        '',
    );
    return withoutNamedDecorations.replace(
        /<rect\b(?=[^>]*\b(?:width)=["'](?:100%|500)["'])(?=[^>]*\b(?:height)=["'](?:100%|500)["'])(?=[^>]*\bstroke=)[^>]*\/?>/gi,
        '',
    );
}

/**
 * Minimum internal clearance (px) from canvas edges for client-owned
 * geometry and labels. Complements GGS G20 safe bounds; does NOT expand viewBox.
 */
export const GRAPH_SAFETY_MARGIN = 30;

/**
 * Cartesian plot padding. Must be ≥ GRAPH_SAFETY_MARGIN and leave room for
 * axis name labels (offset 16px) so all text stays inside [30, 470].
 */
export const CARTESIAN_PLOT_PADDING = 50;

/**
 * Checks if an SVG string is just an "Equation-only item" placeholder
 * that has no visual geometry or coordinate data to show.
 */
export function isEquationOnlySvg(svg: string): boolean {
    if (!svg) return false;
    const lower = svg.toLowerCase();
    return (
        lower.includes('equation-only') ||
        lower.includes('no figure; the stem equation is the presentation ssot') ||
        (lower.includes('<svg') && lower.includes('</svg>') && !lower.includes('<path') && !lower.includes('<line') && !lower.includes('<circle') && !lower.includes('<polygon') && !lower.includes('<text'))
    );
}

/**
 * GGS §3.1.2 display normalizer for server-provided SVG.
 * Enforces fixed/canonical viewBox (prefer 0 0 500 500), responsive width,
 * and preserveAspectRatio="xMidYMid meet". Does NOT expand viewBox for padding
 * (PR-D4-033 Option 1 / G24 — supersedes RS-DL4-032 expansion).
 */
export function addSvgSafetyMargin(svg: string, _padding = GRAPH_SAFETY_MARGIN): string {
    const normalized = svg.replace(/<svg\b([^>]*)>/i, (_openingTag, attributes: string) => {
        const viewBoxMatch = attributes.match(
            /\bviewBox\s*=\s*["']\s*([-+.\d]+)[\s,]+([-+.\d]+)[\s,]+([-+.\d]+)[\s,]+([-+.\d]+)\s*["']/i,
        );
        const widthMatch = attributes.match(/\bwidth\s*=\s*["']\s*([-+.\d]+)/i);
        const heightMatch = attributes.match(/\bheight\s*=\s*["']\s*([-+.\d]+)/i);

        let viewBox = GGS_VIEWBOX;
        if (viewBoxMatch) {
            const minX = Number(viewBoxMatch[1]);
            const minY = Number(viewBoxMatch[2]);
            const width = Number(viewBoxMatch[3]);
            const height = Number(viewBoxMatch[4]);
            // Prefer canonical GGS canvas; otherwise preserve original (no expansion).
            const isCanonical =
                minX === 0 &&
                minY === 0 &&
                width === GGS_CANVAS_SIZE &&
                height === GGS_CANVAS_SIZE;
            const looksLikeLegacyExpanded =
                minX === -GRAPH_SAFETY_MARGIN &&
                minY === -GRAPH_SAFETY_MARGIN &&
                width === GGS_CANVAS_SIZE + GRAPH_SAFETY_MARGIN * 2 &&
                height === GGS_CANVAS_SIZE + GRAPH_SAFETY_MARGIN * 2;
            if (isCanonical || looksLikeLegacyExpanded || (!widthMatch && !heightMatch)) {
                viewBox = GGS_VIEWBOX;
            } else {
                viewBox = `${minX} ${minY} ${width} ${height}`;
            }
        } else {
            const w = Number(widthMatch?.[1] ?? GGS_CANVAS_SIZE);
            const h = Number(heightMatch?.[1] ?? GGS_CANVAS_SIZE);
            viewBox =
                w === GGS_CANVAS_SIZE && h === GGS_CANVAS_SIZE
                    ? GGS_VIEWBOX
                    : `0 0 ${w} ${h}`;
        }

        const cleanedAttributes = attributes
            .replace(/\s+\bviewBox\s*=\s*["'][^"']*["']/i, '')
            .replace(/\s+\bwidth\s*=\s*["'][^"']*["']/i, '')
            .replace(/\s+\bheight\s*=\s*["'][^"']*["']/i, '')
            .replace(/\s+\bpreserveAspectRatio\s*=\s*["'][^"']*["']/i, '')
            .replace(/\s+\bstyle\s*=\s*["'][^"']*["']/i, '');

        return `<svg${cleanedAttributes} viewBox="${viewBox}" width="100%" preserveAspectRatio="xMidYMid meet" style="${GGS_DISPLAY_STYLE}">`;
    });
    return stripCurrentColor(ensureGgsBackgroundPlate(stripDecorativeSvgFrame(normalized)));
}

/** True when a canvas coordinate stays inside the ≥30px internal clearance band. */
export function isInsideSafetyInset(
    x: number,
    y: number,
    canvasSize = GGS_CANVAS_SIZE,
    margin = GRAPH_SAFETY_MARGIN,
): boolean {
    return x >= margin && y >= margin && x <= canvasSize - margin && y <= canvasSize - margin;
}

/** Clamp a canvas coordinate into the safety inset band. */
export function clampToSafetyInset(
    value: number,
    canvasSize = GGS_CANVAS_SIZE,
    margin = GRAPH_SAFETY_MARGIN,
): number {
    return Math.min(canvasSize - margin, Math.max(margin, value));
}

// ── Math Coordinate to SVG Pixel Conversion ──

interface ViewportTransform {
    xToPx: (x: number) => number;
    yToPx: (y: number) => number;
    pxToX: (px: number) => number;
    pxToY: (py: number) => number;
    width: number;
    height: number;
    padding: number;
    plotWidth: number;
    plotHeight: number;
}

export function createTransform(
    viewport: GraphViewport,
    width = GGS_CANVAS_SIZE,
    height = GGS_CANVAS_SIZE,
    padding = CARTESIAN_PLOT_PADDING,
): ViewportTransform {
    const xmin = viewport.xmin ?? -10;
    const xmax = viewport.xmax ?? 10;
    const ymin = viewport.ymin ?? -10;
    const ymax = viewport.ymax ?? 10;

    const plotWidth = width - padding * 2;
    const plotHeight = height - padding * 2;

    const xRange = xmax - xmin || 1;
    const yRange = ymax - ymin || 1;

    const xToPx = (x: number) => padding + ((x - xmin) / xRange) * plotWidth;
    const yToPx = (y: number) => height - padding - ((y - ymin) / yRange) * plotHeight;

    const pxToX = (px: number) => xmin + ((px - padding) / plotWidth) * xRange;
    const pxToY = (py: number) => ymin + ((height - padding - py) / plotHeight) * yRange;

    return { xToPx, yToPx, pxToX, pxToY, width, height, padding, plotWidth, plotHeight };
}

/**
 * Evaluates standard function expressions:
 * Supports:
 * - Linear: y = mx + b, mx + b, y = c, x = c
 * - Quadratic: y = ax^2 + bx + c, a(x-h)^2 + k
 * - Circle: (x-h)^2 + (y-k)^2 = r^2
 * - General polynomial / rational / expressions
 */
interface ParsedFunction {
    type: 'function' | 'vertical_line' | 'circle' | 'point' | 'inequality' | 'raw_points';
    fn?: (x: number) => number;
    xVal?: number;
    circle?: { h: number; k: number; r: number };
    point?: { x: number; y: number; label?: string };
    inequality?: {
        fn: (x: number) => number;
        op: '<' | '<=' | '>' | '>=';
        dashed: boolean;
    };
    rawPoints?: Array<[number, number]>;
    color: string;
    lineWidth: number;
    lineStyle: 'solid' | 'dashed';
    fillOpacity?: number;
    fill?: boolean;
}

function parseLatexExpression(expr: GraphExpression): ParsedFunction | null {
    const raw = (expr.latex || '').trim();
    const color = expr.color || '#2563eb';
    const lineWidth = expr.lineWidth ?? 2.5;
    const lineStyle = expr.lineStyle === 'dashed' ? 'dashed' : 'solid';
    const fillOpacity = expr.fillOpacity ?? 0.15;
    const fill = expr.fill ?? false;

    // Check if coords are explicitly given as a point
    if (expr.coords && expr.coords.length === 2) {
        return {
            type: 'point',
            point: { x: expr.coords[0]!, y: expr.coords[1]!, label: expr.labelText },
            color,
            lineWidth,
            lineStyle,
        };
    }

    // Check (x, y) point format e.g. "(3, 4)" or "\left(3, 4\right)"
    const pointMatch = raw.match(/^(?:\\left\()?\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*(?:\\right\))?$/);
    if (pointMatch) {
        return {
            type: 'point',
            point: { x: parseFloat(pointMatch[1]!), y: parseFloat(pointMatch[2]!), label: expr.labelText },
            color,
            lineWidth,
            lineStyle,
        };
    }

    // Clean latex string: remove \left, \right, \cdot, spaces
    let clean = raw
        .replace(/\\left/g, '')
        .replace(/\\right/g, '')
        .replace(/\\cdot/g, '*')
        .replace(/\\times/g, '*')
        .replace(/\\{/g, '{')
        .replace(/\\}/g, '}')
        .replace(/\s+/g, '');

    // Circle equation: (x - h)^2 + (y - k)^2 = r^2 or x^2 + y^2 = r^2
    const circleMatch1 = clean.match(/^\(x([+-]\d+(?:\.\d+)?)\)\^2\+\(y([+-]\d+(?:\.\d+)?)\)\^2=(\d+(?:\.\d+)?)$/);
    if (circleMatch1) {
        const h = -parseFloat(circleMatch1[1]!);
        const k = -parseFloat(circleMatch1[2]!);
        const r = Math.sqrt(parseFloat(circleMatch1[3]!));
        return { type: 'circle', circle: { h, k, r }, color, lineWidth, lineStyle, fill, fillOpacity };
    }
    const circleMatch2 = clean.match(/^x\^2\+y\^2=(\d+(?:\.\d+)?)$/);
    if (circleMatch2) {
        const r = Math.sqrt(parseFloat(circleMatch2[1]!));
        return { type: 'circle', circle: { h: 0, k: 0, r }, color, lineWidth, lineStyle, fill, fillOpacity };
    }

    // Vertical line: x = c
    const vertMatch = clean.match(/^x=(-?\d+(?:\.\d+)?)$/);
    if (vertMatch) {
        return { type: 'vertical_line', xVal: parseFloat(vertMatch[1]!), color, lineWidth, lineStyle };
    }

    // Inequality check: y <= ..., y >= ..., y < ..., y > ...
    const ineqMatch = clean.match(/^y(\\le|\\ge|<=|>=|<|>)(.+)$/);
    if (ineqMatch) {
        const opStr = ineqMatch[1]!;
        const rhs = ineqMatch[2]!;
        const op: '<' | '<=' | '>' | '>=' =
            opStr === '\\le' || opStr === '<=' ? '<=' :
            opStr === '\\ge' || opStr === '>=' ? '>=' :
            opStr === '<' ? '<' : '>';
        const dashed = op === '<' || op === '>';
        const fn = compileExpressionRhs(rhs);
        if (fn) {
            return {
                type: 'inequality',
                inequality: { fn, op, dashed },
                color,
                lineWidth,
                lineStyle: dashed ? 'dashed' : 'solid',
                fillOpacity,
            };
        }
    }

    // Standard y = f(x) or raw f(x)
    let rhs = clean;
    if (clean.startsWith('y=')) {
        rhs = clean.slice(2);
    } else if (clean.startsWith('f(x)=')) {
        rhs = clean.slice(5);
    }

    const fn = compileExpressionRhs(rhs);
    if (fn) {
        return { type: 'function', fn, color, lineWidth, lineStyle, fill, fillOpacity };
    }

    return null;
}

/**
 * Compiles a mathematical expression string into a JS function (x) => y
 */
function compileExpressionRhs(expr: string): ((x: number) => number) | null {
    try {
        // Convert LaTeX fractions \frac{a}{b} -> ((a)/(b))
        let jsExpr = expr.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '(($1)/($2))');

        // Convert powers x^2 -> (x**2), (x-1)^2 -> ((x-1)**2)
        jsExpr = jsExpr.replace(/\^(\d+)/g, '**$1');
        jsExpr = jsExpr.replace(/\^\{([^}]+)\}/g, '**($1)');

        // Convert \sqrt{a} -> Math.sqrt(a)
        jsExpr = jsExpr.replace(/\\sqrt\{([^}]+)\}/g, 'Math.sqrt($1)');

        // Convert implicit multiplication: e.g. 2x -> 2*x, -3x -> -3*x, 4(x) -> 4*(x), (x+1)(x-2) -> (x+1)*(x-2)
        jsExpr = jsExpr.replace(/(\d)x/g, '$1*x');
        jsExpr = jsExpr.replace(/(\d)\(/g, '$1*(');
        jsExpr = jsExpr.replace(/\)x/g, ')*x');
        jsExpr = jsExpr.replace(/\)\(/g, ')*(');

        // Sanitize: allow only numbers, x, basic arithmetic, and Math functions
        const validPattern = /^[0-9x\s+\-*/().*Mathsqrtabsincostan]+$/;
        if (!validPattern.test(jsExpr.replace(/Math\.sqrt/g, 'Mathsqrt'))) {
            // If complex syntax, fallback to safe linear/quadratic parser
            return fallbackParsePolynomial(expr);
        }

        // Safe Function constructor
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        const evaluator = new Function('x', `
            try {
                const y = ${jsExpr};
                return (typeof y === 'number' && !isNaN(y) && isFinite(y)) ? y : NaN;
            } catch {
                return NaN;
            }
        `) as (x: number) => number;

        // Test with sample value
        evaluator(0);
        return evaluator;
    } catch {
        return fallbackParsePolynomial(expr);
    }
}

/**
 * Fallback parser for linear (mx + b) and quadratic (ax^2 + bx + c) expressions
 */
function fallbackParsePolynomial(expr: string): ((x: number) => number) | null {
    // Linear: mx + b or -x + b or x - b
    const linearMatch = expr.match(/^([+-]?\d*(?:\.\d+)?)x\s*([+-]\s*\d+(?:\.\d+)?)?$/);
    if (linearMatch) {
        const mStr = linearMatch[1] || '';
        const m = mStr === '' || mStr === '+' ? 1 : mStr === '-' ? -1 : parseFloat(mStr);
        const bStr = (linearMatch[2] || '').replace(/\s+/g, '');
        const b = bStr ? parseFloat(bStr) : 0;
        return (x: number) => m * x + b;
    }

    // Constant: y = c
    const constMatch = expr.match(/^[+-]?\d+(?:\.\d+)?$/);
    if (constMatch) {
        const c = parseFloat(constMatch[0]);
        return () => c;
    }

    return null;
}

/**
 * Native SVG Cartesian Coordinate Plane Component
 */
export function NativeGraphRenderer({
    graphData,
    svgFallback = null,
    className = '',
    height = 320,
    width = '100%',
}: NativeGraphRendererProps) {
    // 1. Direct SVG Render if provided and valid
    const validSvg = useMemo(() => {
        const svgContent = svgFallback || (graphData as Record<string, unknown> | null)?.svg as string | null;
        if (svgContent && typeof svgContent === 'string' && svgContent.trim().length > 0) {
            if (!isEquationOnlySvg(svgContent)) {
                return addSvgSafetyMargin(svgContent);
            }
        }
        return null;
    }, [svgFallback, graphData]);

    if (validSvg) {
        return (
            <div
                className={`ggs-canvas-wrapper native-graph native-graph--svg ${className}`.trim()}
                style={{
                    ...GGS_WRAPPER_INLINE_STYLE,
                    width: width ?? '100%',
                    maxWidth: '100%',
                    maxHeight: typeof height === 'number' ? height : undefined,
                    height: '100%',
                    margin: '0 auto',
                }}
                dangerouslySetInnerHTML={{ __html: validSvg }}
            />
        );
    }

    // 2. Cartesian Coordinate Plotting if viewport/expressions are available
    const viewport: GraphViewport = graphData?.viewport ?? { xmin: -10, xmax: 10, ymin: -10, ymax: 10 };
    const expressions = graphData?.expressions ?? [];
    const interactivePoints: InteractivePoint[] = graphData?.interactivePoints ?? [];

    // If there are no expressions and no interactive points, and viewport is default, return null
    if (expressions.length === 0 && interactivePoints.length === 0) {
        return null;
    }

    const SVG_SIZE = GGS_CANVAS_SIZE;
    // GGS §3.1 + internal clearance: plot padding ≥ GRAPH_SAFETY_MARGIN (30px)
    const transform = createTransform(viewport, SVG_SIZE, SVG_SIZE, CARTESIAN_PLOT_PADDING);

    // Compute grid ticks
    const xSpan = (viewport.xmax ?? 10) - (viewport.xmin ?? -10);
    const ySpan = (viewport.ymax ?? 10) - (viewport.ymin ?? -10);

    const stepX = xSpan <= 10 ? 1 : xSpan <= 25 ? 2 : 5;
    const stepY = ySpan <= 10 ? 1 : ySpan <= 25 ? 2 : 5;

    const xTicks: number[] = [];
    const minX = Math.ceil((viewport.xmin ?? -10) / stepX) * stepX;
    const maxX = Math.floor((viewport.xmax ?? 10) / stepX) * stepX;
    for (let x = minX; x <= maxX; x += stepX) {
        if (Math.abs(x) > 0.001) xTicks.push(x);
    }

    const yTicks: number[] = [];
    const minY = Math.ceil((viewport.ymin ?? -10) / stepY) * stepY;
    const maxY = Math.floor((viewport.ymax ?? 10) / stepY) * stepY;
    for (let y = minY; y <= maxY; y += stepY) {
        if (Math.abs(y) > 0.001) yTicks.push(y);
    }

    const originPxX = transform.xToPx(0);
    const originPxY = transform.yToPx(0);

    // Parse all expressions
    const parsedExpressions: ParsedFunction[] = expressions
        .map(parseLatexExpression)
        .filter((item): item is ParsedFunction => item !== null);

    return (
        <div
            className={`ggs-canvas-wrapper native-graph native-graph--cartesian ${className}`.trim()}
            style={{
                ...GGS_WRAPPER_INLINE_STYLE,
                width: width ?? '100%',
                maxWidth: '100%',
                maxHeight: typeof height === 'number' ? height : undefined,
                height: '100%',
                margin: '0 auto',
            }}
        >
            <svg
                viewBox={GGS_VIEWBOX}
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
                className="w-full h-full select-none native-graph__svg ggs-svg-root"
                style={{
                    maxWidth: '100%',
                    maxHeight: typeof height === 'number' ? height : '100%',
                    backgroundColor: '#ffffff',
                    colorScheme: 'only light',
                    forcedColorAdjust: 'none',
                }}
                aria-label="Coordinate Graph"
                role="img"
            >
                {/* GGS Layer 1 — self-contained light canvas plate */}
                <rect width="100%" height="100%" fill="#FFFFFF" />
                <defs>
                    {/* Arrowhead marker for axes */}
                    <marker
                        id="axis-arrow-x"
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                    >
                        <path d="M 0 1.5 L 10 5 L 0 8.5 z" fill="#1e293b" />
                    </marker>
                    <marker
                        id="axis-arrow-y"
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                    >
                        <path d="M 0 1.5 L 10 5 L 0 8.5 z" fill="#1e293b" />
                    </marker>
                </defs>

                {/* Cartesian grid is confined to the axis box (padding inset). */}
                <g id="grid-lines" stroke="#e2e8f0" strokeWidth="1">
                    {xTicks.map((x) => (
                        <line
                            key={`grid-x-${x}`}
                            x1={transform.xToPx(x)}
                            y1={transform.padding}
                            x2={transform.xToPx(x)}
                            y2={SVG_SIZE - transform.padding}
                        />
                    ))}
                    {yTicks.map((y) => (
                        <line
                            key={`grid-y-${y}`}
                            x1={transform.padding}
                            y1={transform.yToPx(y)}
                            x2={SVG_SIZE - transform.padding}
                            y2={transform.yToPx(y)}
                        />
                    ))}
                </g>

                {/* Shaded Inequality Regions */}
                {parsedExpressions
                    .filter((p) => p.type === 'inequality')
                    .map((p, idx) => {
                        const ineq = p.inequality!;
                        const pointsTop: string[] = [];
                        const pointsBottom: string[] = [];
                        const numSamples = 100;
                        const xmin = viewport.xmin;
                        const xmax = viewport.xmax;
                        const dx = (xmax - xmin) / numSamples;

                        for (let i = 0; i <= numSamples; i++) {
                            const x = xmin + i * dx;
                            const y = ineq.fn(x);
                            if (!isNaN(y)) {
                                const px = transform.xToPx(x);
                                const py = transform.yToPx(y);
                                pointsTop.push(`${px},${py}`);
                                if (ineq.op === '>=' || ineq.op === '>') {
                                    pointsBottom.push(`${px},${transform.padding}`);
                                } else {
                                    pointsBottom.push(`${px},${SVG_SIZE - transform.padding}`);
                                }
                            }
                        }

                        if (pointsTop.length < 2) return null;
                        const polyPoints = `${pointsTop.join(' ')} ${pointsBottom.reverse().join(' ')}`;

                        return (
                            <polygon
                                key={`ineq-shade-${idx}`}
                                points={polyPoints}
                                fill={p.color}
                                fillOpacity={p.fillOpacity ?? 0.15}
                            />
                        );
                    })}

                {/* Main Axes */}
                <g id="axes" stroke="#1e293b" strokeWidth="1.75">
                    {/* X-Axis */}
                    <line
                        x1={transform.padding - 10}
                        y1={originPxY}
                        x2={SVG_SIZE - transform.padding + 12}
                        y2={originPxY}
                        markerEnd="url(#axis-arrow-x)"
                    />
                    {/* Y-Axis */}
                    <line
                        x1={originPxX}
                        y1={SVG_SIZE - transform.padding + 10}
                        x2={originPxX}
                        y2={transform.padding - 12}
                        markerEnd="url(#axis-arrow-y)"
                    />
                </g>

                {/* Axis Tick Marks and Labels — GGS §4.2.2 middle/central anchors */}
                <g id="axis-ticks" fill="#0f172a" fontSize="12" fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="600">
                    {/* X Ticks & Numbers */}
                    {xTicks.map((x) => {
                        const px = transform.xToPx(x);
                        const labelX = clampToSafetyInset(px);
                        const labelY = clampToSafetyInset(originPxY + 16);
                        return (
                            <g key={`tick-x-${x}`}>
                                <line
                                    x1={px}
                                    y1={originPxY - 4}
                                    x2={px}
                                    y2={originPxY + 4}
                                    stroke="#334155"
                                    strokeWidth="1.5"
                                />
                                <text
                                    x={labelX}
                                    y={labelY}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="#0f172a"
                                    fontWeight="600"
                                >
                                    {x}
                                </text>
                            </g>
                        );
                    })}

                    {/* Y Ticks & Numbers */}
                    {yTicks.map((y) => {
                        const py = transform.yToPx(y);
                        const labelX = clampToSafetyInset(originPxX - 16);
                        const labelY = clampToSafetyInset(py);
                        return (
                            <g key={`tick-y-${y}`}>
                                <line
                                    x1={originPxX - 4}
                                    y1={py}
                                    x2={originPxX + 4}
                                    y2={py}
                                    stroke="#334155"
                                    strokeWidth="1.5"
                                />
                                <text
                                    x={labelX}
                                    y={labelY}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="#0f172a"
                                    fontWeight="600"
                                >
                                    {y}
                                </text>
                            </g>
                        );
                    })}

                    {/* Origin 0 label */}
                    <text
                        x={clampToSafetyInset(originPxX - 16)}
                        y={clampToSafetyInset(originPxY + 16)}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="#0f172a"
                        fontWeight="600"
                        fontSize="11"
                    >
                        0
                    </text>

                    {/* X & Y Axis Name Labels */}
                    <text
                        x={clampToSafetyInset(SVG_SIZE - transform.padding + 16)}
                        y={clampToSafetyInset(originPxY)}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="#0f172a"
                        fontWeight="700"
                        fontSize="14"
                        fontStyle="italic"
                    >
                        x
                    </text>
                    <text
                        x={clampToSafetyInset(originPxX)}
                        y={clampToSafetyInset(transform.padding - 16)}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="#0f172a"
                        fontWeight="700"
                        fontSize="14"
                        fontStyle="italic"
                    >
                        y
                    </text>
                </g>

                {/* Plotted Function Paths */}
                <g id="plotted-functions">
                    {parsedExpressions.map((expr, idx) => {
                        if (expr.type === 'vertical_line' && expr.xVal !== undefined) {
                            const px = transform.xToPx(expr.xVal);
                            return (
                                <line
                                    key={`vert-${idx}`}
                                    x1={px}
                                    y1={transform.padding}
                                    x2={px}
                                    y2={SVG_SIZE - transform.padding}
                                    stroke={expr.color}
                                    strokeWidth={expr.lineWidth}
                                    strokeDasharray={expr.lineStyle === 'dashed' ? '6,5' : undefined}
                                />
                            );
                        }

                        if (expr.type === 'circle' && expr.circle) {
                            const cx = transform.xToPx(expr.circle.h);
                            const cy = transform.yToPx(expr.circle.k);
                            const rx = (expr.circle.r / (viewport.xmax - viewport.xmin)) * transform.plotWidth;
                            return (
                                <circle
                                    key={`circ-${idx}`}
                                    cx={cx}
                                    cy={cy}
                                    r={rx}
                                    stroke={expr.color}
                                    strokeWidth={expr.lineWidth}
                                    strokeDasharray={expr.lineStyle === 'dashed' ? '6,5' : undefined}
                                    fill={expr.fill ? expr.color : 'none'}
                                    fillOpacity={expr.fillOpacity ?? 0.15}
                                />
                            );
                        }

                        const fn = expr.fn || expr.inequality?.fn;
                        if (fn) {
                            // Generate smooth curve sample points
                            const numSamples = 200;
                            const xmin = viewport.xmin;
                            const xmax = viewport.xmax;
                            const dx = (xmax - xmin) / numSamples;
                            const segments: Array<Array<[number, number]>> = [];
                            let currentSegment: Array<[number, number]> = [];

                            for (let i = 0; i <= numSamples; i++) {
                                const x = xmin + i * dx;
                                const y = fn(x);
                                if (!isNaN(y) && isFinite(y) && y >= viewport.ymin - 5 && y <= viewport.ymax + 5) {
                                    const px = transform.xToPx(x);
                                    const py = transform.yToPx(y);
                                    currentSegment.push([px, py]);
                                } else {
                                    if (currentSegment.length > 1) {
                                        segments.push(currentSegment);
                                    }
                                    currentSegment = [];
                                }
                            }
                            if (currentSegment.length > 1) {
                                segments.push(currentSegment);
                            }

                            return segments.map((seg, sIdx) => {
                                const pathD = seg.reduce(
                                    (acc, [x, y], i) => (i === 0 ? `M ${x.toFixed(1)} ${y.toFixed(1)}` : `${acc} L ${x.toFixed(1)} ${y.toFixed(1)}`),
                                    '',
                                );
                                return (
                                    <path
                                        key={`fn-${idx}-${sIdx}`}
                                        d={pathD}
                                        fill="none"
                                        stroke={expr.color}
                                        strokeWidth={expr.lineWidth}
                                        strokeDasharray={expr.lineStyle === 'dashed' ? '6,5' : undefined}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                );
                            });
                        }

                        return null;
                    })}
                </g>

                {/* Interactive Points and Expression Points */}
                <g id="points">
                    {parsedExpressions
                        .filter((p) => p.type === 'point' && p.point)
                        .map((p, idx) => {
                            const pt = p.point!;
                            const px = transform.xToPx(pt.x);
                            const py = transform.yToPx(pt.y);
                            return (
                                <g key={`pt-${idx}`}>
                                    <circle
                                        cx={px}
                                        cy={py}
                                        r={5}
                                        fill={p.color}
                                        stroke="#ffffff"
                                        strokeWidth="2"
                                    />
                                    {pt.label && (
                                        <text
                                            x={clampToSafetyInset(px + 8)}
                                            y={clampToSafetyInset(py - 8)}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="#0f172a"
                                            fontSize="13"
                                            fontWeight="600"
                                        >
                                            {pt.label}
                                        </text>
                                    )}
                                </g>
                            );
                        })}

                    {interactivePoints.map((pt, idx) => {
                        const px = transform.xToPx(pt.x);
                        const py = transform.yToPx(pt.y);
                        return (
                            <g key={`ipt-${idx}`}>
                                <circle
                                    cx={px}
                                    cy={py}
                                    r={5.5}
                                    fill="#2563eb"
                                    stroke="#ffffff"
                                    strokeWidth="2"
                                />
                                {pt.label && (
                                    <text
                                        x={clampToSafetyInset(px + 8)}
                                        y={clampToSafetyInset(py - 8)}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                        fill="#0f172a"
                                        fontSize="13"
                                        fontWeight="600"
                                    >
                                        {pt.label}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </g>
            </svg>
        </div>
    );
}

export default NativeGraphRenderer;
